import {GameStateEnums} from '../../../../../shared/GameEnums'
import {Ease} from '@colonist/pixi'
import {UIGameMechanic} from '../UIGameEventController'
import {UIGameManager} from '../UIGameManager'
import {UIGameDiceView} from './UIGameDiceView'
import {UIGameBeginnerModeDiceHelper} from '../ui_game_beginner_mode/ui_game_beginner_mode_helpers/UIGameBeginnerModeDiceHelper'
import {ActionState, PlayerColor, TurnState} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {UIGamePlayerInformationView} from '../ui_game_players/UIGamePlayerInformationView'
import {StorageWrapper} from '../../StorageWrapper'

export abstract class UIGameDiceController implements UIGameMechanic {
    private readonly animationTime: number = 300
    private currentTurnPlayer: PlayerColor | undefined

    protected readonly uiGameManager: UIGameManager
    protected readonly easeInAnimation: Ease
    protected easeAnimationCount: number = 0 // When the dice starts animating, allows the dice to ease into the animation
    protected isPlayerDiceAnimating: boolean

    /**
     * @deprecated Will be removed once Pixi.js Mobile Player Information view is removed
     */
    protected opponentDiceViews: UIGameDiceView[]
    private currentDiceView: UIGameDiceView

    private readonly isSinglePlayerGame: boolean = false

    private beginnerModeHelper?: UIGameBeginnerModeDiceHelper

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.isPlayerDiceAnimating = false
        this.easeInAnimation = new Ease({useRef: false})
        this.isSinglePlayerGame = this.uiGameManager.gameState.players.length == 1
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.DiceStateUpdated: this.diceStateUpdated(); break
            case UIGameEventEnums.GameStateUpdated: this.gameStateUpdated(); break
            case UIGameEventEnums.CanvasResized: this.redrawDiceViews(); break
            case UIGameEventEnums.EndOfInitialPlacement: this.redrawDiceViews(); break
            default: break
        }
    }

    /**
     * @deprecated Will be removed once Pixi.js Mobile Player Information view is removed
     */
    initBeginnerModeHelper() {
        this.beginnerModeHelper = new UIGameBeginnerModeDiceHelper(this.uiGameManager)
    }

    protected abstract initializeOpponentDiceViews()

    protected initializeCurrentTurnPlayer() {
        if(this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement) return
        this.currentTurnPlayer = this.uiGameManager.gameController.currentState.currentTurnPlayerColor
    }

    protected redrawDiceViews() {
        this.opponentDiceViews.forEach((view: UIGameDiceView) => view.destroy())
        this.initializeCurrentTurnPlayer()
        this.initializeOpponentDiceViews()
        this.updateDiceView()
    }

    protected diceStateUpdated() {
        this.updateDiceImage()
    }

    protected updateDiceImage() {
        this.updateDiceTexture()
        if(!this.uiGameManager.gameController.diceState.diceThrown) return

        for(const view of this.opponentDiceViews) this.startDiceNumberAnimation(view)
    }

    private gameStateUpdated() {
        const shouldHidePlayerDice = this.uiGameManager.gameController.currentState.turnState == TurnState.InitialPlacement || this.beginnerModeHelper?.isDiceHiddenPermanently
        if(shouldHidePlayerDice) return this.hideAllDiceViews()

        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Dice) return

        if(this.shouldUpdateDiceView() == false) return

        const currentTurnPlayerColor = this.uiGameManager.gameController.currentState.currentTurnPlayerColor
        this.currentTurnPlayer = currentTurnPlayerColor
        this.hideCurrentDiceView()
        this.updateDiceView()
    }

    private shouldUpdateDiceView(): boolean {
        const currentTurnPlayerColor = this.uiGameManager.gameController.currentState.currentTurnPlayerColor
        if(this.isSinglePlayerGame && this.uiGameManager.gameController.isMyTurn) return true
        if(this.beginnerModeHelper != undefined) return true
        if(currentTurnPlayerColor == this.currentTurnPlayer) return false
        return true
    }

    private hideCurrentDiceView() {
        if(this.currentDiceView == undefined) return

        if(this.shouldSkipDiceAnimation()) {
            this.currentDiceView.hide()
        } else {
            this.currentDiceView.easeOut(this.animationTime)
        }
    }

    protected updateDiceView() {
        if(!this.uiGameManager.gameController.isMyTurn) {
            this.makeOpponentDiceVisible()
        }
    }

    private hideAllDiceViews() {
        this.opponentDiceViews.forEach((view: UIGameDiceView) => view.hide())
    }

    protected makeOpponentDiceVisible() {
        if(this.opponentDiceViews.length == 0) return
        if(this.uiGameManager.playersController == undefined) return
        const playerInformationViews = this.uiGameManager.playersController.playerInformationViews
        const currentTurnPlayerIndex = playerInformationViews.findIndex((view: UIGamePlayerInformationView) => view.player.color == this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
        const opponentCount = playerInformationViews.length - 1
        if(this.uiGameManager.gameController.isPlayerSpectator && currentTurnPlayerIndex == opponentCount) return

        const opponentDiceViewToDisplay = this.getOpponentDiceViewToDisplay(currentTurnPlayerIndex, opponentCount)
        if(opponentDiceViewToDisplay == undefined) return
        if(this.currentDiceView == opponentDiceViewToDisplay) this.easeInAnimation.removeAll()
        this.currentDiceView = opponentDiceViewToDisplay

        if(this.shouldSkipDiceAnimation()) {
            opponentDiceViewToDisplay.show()
        } else {
            opponentDiceViewToDisplay.easeIn(this.animationTime)
        }
    }

    private shouldSkipDiceAnimation(): boolean {
        const isReplay = this.uiGameManager.gameController.isReplay
        const isAnimationsEnabled = StorageWrapper.getAnimation() ?? true
        return isReplay && !isAnimationsEnabled
    }

    protected updateDiceTexture() {
        this.opponentDiceViews.forEach((view: UIGameDiceView) => view.updateDiceTexture())
    }

    protected currentActionStateIsEmpty(): boolean {
        return this.uiGameManager.gameController.currentState.actionState == ActionState.None
    }

    protected getOpponentDiceViewToDisplay(currentTurnPlayerIndex: number, opponentCount: number): UIGameDiceView {
        const topOpponentDiceView = this.opponentDiceViews[0]
        const midOpponentDiceView = this.opponentDiceViews[1]
        const bottomOpponentDiceView = this.opponentDiceViews[2]

        switch(currentTurnPlayerIndex) {
            case opponentCount - 1: return bottomOpponentDiceView
            case opponentCount - 2: return midOpponentDiceView
            case 0: return topOpponentDiceView
            default: return midOpponentDiceView
        }
    }

    private startDiceNumberAnimation(diceView: UIGameDiceView) {
        if(diceView.parent == undefined) return
        if(!this.uiGameManager.gameController.diceState.diceThrown) return

        if(this.beginnerModeHelper == undefined) return this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.DiceNumberAnimationEnd)
        this.beginnerModeHelper.startDiceNumberAnimation(diceView)
    }
}
